import TotalCMS from '../totalcms';
import TotalField from './totalfield';
import TotalDispatcher from './dispatcher';
import FieldVisibility from './field-visibility';
import Identifier from './identifier';
import tcmsConfirm from '../confirm-dialog';
import { t } from '../i18n';
import { csrfHeadersFor } from '../csrf';
import Checkbox from './checkbox';
import RadioField from './radio';
import Textarea from './textarea';
import NumberField from './number';
import PriceField from './price';
import ColorField from './color';
import DateField from './date';
import CardField from './card';
import DeckField from './deck';
import DeckTableField from './deckTable';
import PasswordField from './password';
import SelectField from './select';
import MultiSelectField from './multiselect';
import ChecklistField from './checklist';
import ListField from './list';
import RangeSlider from './range';
import StyledTextField from './styledtext';
import LocalizedTextField from './localizedtext';
import LocalizedStyledTextField from './localizedstyledtext';
import SVGField from './svg';
import ImageField from './image';
import GalleryField from './gallery';
import PropertiesField from './properties';
import CustomPropertiesField from './customProperties';
import SchemaPropertiesField from './schemaProperties';
import JSONField from './json';
import FileField from './file';
import DepotField from './depot';
import DepotDropField from './depot-drop';
import CodeField from './code';
import SecretField from './secret';

// import Deck from './deck';
// import MarkdownField from './markdown';

//-----------------------------------------------
// Total CMS Form constructor
//-----------------------------------------------
export default class TotalForm {

    // Constructors
    constructor(formRef, settings = {}) {
        this.form = this.setForm(formRef);
		formRef.totalform = this;
		this.fields = []; // Initialize early to prevent undefined errors on disabled forms

		if (formRef.dataset.disabled !== undefined) {
			return;
		}

		if (!formRef || !this.form) {
			console.error("form not found");
			return;
		}

		// Define option defaults
		const defaults = {
			actions : {
				new    : [],
				edit   : [],
				delete : [],
			}
		};
		this.settings = Object.assign({}, defaults, settings);

		if (this.form.dataset.newActions) {
			this.settings.actions.new = JSON.parse(this.form.dataset.newActions);
		}
		if (this.form.dataset.editActions) {
			this.settings.actions.edit = JSON.parse(this.form.dataset.editActions);
		}
		if (this.form.dataset.deleteActions) {
			this.settings.actions.delete = JSON.parse(this.form.dataset.deleteActions);
		}
		this.extensionActions = {};
		if (this.form.dataset.extensionActions) {
			this.extensionActions = JSON.parse(this.form.dataset.extensionActions);
		}
		this.delayActions = 2000;

		this.api = new TotalCMS({
			url: this.form.dataset.api,
		});
		this.route      = this.form.dataset.route;
		this.method     = this.form.dataset.method || "POST";
		this.id         = this.form.dataset.id||"";
		this.collection = this.form.dataset.collection;
		this.schema     = this.form.dataset.schema;
		this.states     = ["unsaved","success","error","processing"];
		this.state      = null;
		this.validated  = false;

		// Check if the form is a collection, schema or object
		this.type = this.form.dataset.form || "object";

		// Check if the form has an ID set
		// This is done before fields are created in case the ID is autogenerated
		const formIDSet = (typeof this.form.dataset.id !== "undefined" && this.form.dataset.id.length > 0);

		// If an ID is set, we are in edit mode
		if (formIDSet) this.setupEditMode();

		this.processFields();
		this.setupFieldsForEdit();

		this.dispatcher = new TotalDispatcher(this.form);
		this.visibility = new FieldVisibility(this.form, this.fields);

        this.eventListeners();
        this.registerButtons();
		this.visibility.initialize();

		// Signal that all fields are initialized and form is ready
		this.form.dispatchEvent(new CustomEvent('totalform:ready'));

		if (this.isObjectForm() && !this.isEditMode()) {
			this.focusFirstInput();
		}

		if (this.form.classList.contains("autosave")) {
			this.autosave = true;
		}

		// Check if this form uses AJAX (default is true if not specified)
		const usesAjax = this.form.dataset.ajax !== "false";

		// Check if unsaved changes warning should be shown (default is true if not specified)
		const warnUnsaved = this.form.classList.contains("no-unsaved-warning") ? false : true;

		// Don't run the beforeunload event when inside iframes, for non-AJAX forms, or when explicitly disabled
		// Non-AJAX forms handle their own submission and don't need unsaved change warnings
		if (window === window.top && usesAjax && warnUnsaved) {
			window.onbeforeunload = e => {
				if (this.isUnsaved()) {
					e.preventDefault();
					const dialogText = "There are unsaved changes";
					e.returnValue = dialogText;
					return dialogText;
				}
			};
		}
    }

    //-------------------------
    // Utility Methods
    //-------------------------

	isDeckForm() {
		return this.type === "deck";
	}
	isObjectForm() {
		return this.type === "object";
	}
	isCollectionForm() {
		return this.type === "collection";
	}
	isSchemaForm() {
		return this.type === "schema";
	}
	isTemplateForm() {
		return this.type === "template";
	}
	isTemplateEditMode() {
		return this.isTemplateForm() && this.method.toUpperCase() === "PUT";
	}
    // Check to see if the object is a HTML node.
    isDomNode(node){
        return node && typeof node === "object" && "nodeType" in node && node.nodeType === 1;
    }
    // Set the form via a DOM element or selector string
    setForm(formRef) {
        switch(typeof formRef) {
            case "string":
                return document.getElementById(formRef);
            case "object":
                if (this.isDomNode(formRef)){
                    return formRef;
                }
                break;
        }
        return null;
    }

	setId(id) {
		this.id = id;
		this.form.dataset.id = id;
	}

	// Extract the saved object id from a save response. Object saves are
	// Fractal data-wrapped ({ data: { id, ... } }); older code read response.id
	// (undefined), so an add-only form never learned its autogen id.
	responseId(response) {
		return response?.data?.id ?? response?.id ?? null;
	}

	getId() {
		if (this.isEditMode()) return this.id;

		// Once a save has assigned an id (e.g. an add-only form whose id field was
		// removed for autogen), prefer it — there may be no id field to read back.
		if (this.id && this.id.length > 0) return this.id;

		// Filter for main form's ID field, excluding subfields (like deck item IDs)
		const idField = this.fields.filter(field => {
			return field.property === "id" && !field.isSubField();
		}).shift();
		if (idField) return idField.getValue();

		// Fall back to a direct DOM lookup — this.fields may be empty when
		// getId() is called during the recursive processFields() bootstrap
		// (image/file edit dialogs re-enter processFields before the outer
		// call assigns this.fields).
		const idInputs    = Array.from(this.form.querySelectorAll('input[name="id"]'));
		const formIdInput = idInputs.find(input => !input.closest('dialog'));
		if (formIdInput) return formIdInput.value;

		console.warn("ID field not found");
		return null;
	}

    //-------------------------
    // Init Form
    //-------------------------
    processFields() {
		const fields = Array.from(this.form.getElementsByClassName("form-field"));
		const fieldObjects = [];
		const dropletObjects = [];
        fields.forEach(field => {
			// Skip fields inside <template> elements (e.g., deck templates)
			if (field.closest('template')) {
				return;
			}

			let totalfield = field.totalfield;
			if (!totalfield) {
				try {
					totalfield = this.generateFieldObject(field);
				} catch (e) {
					const inputName = field.querySelector("input,textarea,select")?.name;
					console.warn(`Failed to process field [${field.dataset.type}] name="${inputName}":`, e, field);
					return;
				}
				if (totalfield === null) return;
			}

			if (!totalfield.isSubField()) {
				fieldObjects.push(totalfield);
			}
			// Droplets must bypass the subfield filter so saveDroplets() can flush
			// queued uploads for image/file fields nested inside cards and deck items
			// after the parent object's ID is assigned.
			if (totalfield.isDroplet()) {
				dropletObjects.push(totalfield);
			}
        });
        this.fields = fieldObjects;
        this.droplets = dropletObjects;
    }

	// Re-scan the form for field nodes after the caller has mutated the DOM
	// (e.g. swapping a text input for a select when a country changes).
	// processFields() preserves existing TotalField instances via the
	// field.totalfield check, so only newly-added .form-field nodes get fresh
	// objects and removed ones drop out of this.fields.
	refreshFields() {
		this.processFields();
		this.droplets = this.fields.filter(field => field.isDroplet());
		this.setupFieldsForEdit();
		this.visibility.fields = this.fields;
		this.visibility.initialize();
		this.form.dispatchEvent(new CustomEvent('totalform:refresh'));
	}

    registerButtons() {
		// Save button action is handled by the TotalFormManager

		const deleteButtons = Array.from(this.form.querySelectorAll("button.cms-delete,a.cms-delete,.cms-delete a,.cms-delete button"));
        deleteButtons.forEach(button => {
            button.addEventListener("click", event => {
                event.preventDefault();
				this.delete();
            });
        });
    }

    generateFieldObject(field) {
        const settings = JSON.parse(field.dataset.settings||"{}");
        settings.form = this;

        switch (field.dataset.type) {
			case "id":
			case "slug":
                return new Identifier(field, settings);

			case "text":
			case "time":
            case "url":
			case "hidden":
			case "email":
			case "phone":
				return new TotalField(field, settings);

			case "textarea":
				return new Textarea(field, settings);

            case "checkbox":
            case "toggle":
                return new Checkbox(field, settings);

			case "checklist":
			case "multicheckbox":
				return new ChecklistField(field, settings);

            case "radio":
                return new RadioField(field, settings);

            case "number":
                return new NumberField(field, settings);

            case "price":
                return new PriceField(field, settings);

            case "color":
                return new ColorField(field, settings);

            case "date":
			case "datetime":
                return new DateField(field, settings);

            case "select":
                return new SelectField(field, settings);

            case "multiselect":
                return new MultiSelectField(field, settings);

            case "list":
                return new ListField(field, settings);

			case "password":
				return new PasswordField(field, settings);

			case "secret":
				return new SecretField(field, settings);

            case "range":
                return new RangeSlider(field, settings);

			case "styledtext":
                return new StyledTextField(field, settings);

			case "localizedtext":
			case "localizedtextarea":
				// Same JS class for both — getValue/setValue queries
				// `input[data-locale], textarea[data-locale]` and works
				// uniformly. The PHP FormField subclass decides the shape.
				return new LocalizedTextField(field, settings);

			case "localizedstyledtext":
				return new LocalizedStyledTextField(field, settings);

            case "svg":
                return new SVGField(field, settings);

			case "image":
				return new ImageField(field,settings);

			case "gallery":
				return new GalleryField(field,settings);

			case "json":
				return new JSONField(field,settings);

			case "file":
				return new FileField(field,settings);

			case "depot":
				return new DepotField(field,settings);

			case "depotDrop":
				return new DepotDropField(field,settings);

			case "code":
				return new CodeField(field,settings);

			case "card":
				return new CardField(field, settings);

			case "deck":
                return new DeckField(field, settings);

			case "deckTable":
				return new DeckTableField(field, settings);

			// case "markdown":
            //     return new MarkdownField(field, settings);

			case "properties":
				return new PropertiesField(field,settings);

			case "customProperties":
				return new CustomPropertiesField(field,settings);

			case "schemaProperties":
				return new SchemaPropertiesField(field,settings);

            default:
                console.warn("Unknown field",field);
				return new TotalField(field, settings);
        }
    }

    //-------------------------
    // Submit functions
    //-------------------------
    eventListeners() {
		// Prevent the default form submission
		if (this.method.toUpperCase() !== "GET") {
			this.form.addEventListener("submit", event => event.preventDefault());
		}
		this.form.addEventListener("field-change", e => {
			// Ignore change events when form is processing
			// If a field is in focus on submit, a change event is triggered
			if (this.isProcessing()) return;
			this.unsaved();
			if (this.autosave) this.save();
		});
		this.form.addEventListener("field-error", e => this.error(e.detail.message, e.detail.field));
    }

	validate() {
		let isValid = true;

		this.fields.forEach(field => {
			if (!field.validate()) {
				isValid = false;
			}
		});

		if (isValid && this.form.checkValidity()) {
			this.clearErrorSummary();
			return true;
		}

		// Collect all invalid fields and show error summary
		const invalidFields = this.getInvalidFields();
		this.showErrorSummary(invalidFields);

		return false;
	}

	getInvalidFields() {
		return this.fields.filter(field => {
			if (!field.isVisible()) return false;
			if (field.isSubField()) return false;
			return !field.input.checkValidity();
		});
	}

	getErrorSummaryContainer() {
		// Check for custom selector via data attribute
		const customSelector = this.form.dataset.errorSummary;
		if (customSelector) {
			const custom = document.querySelector(customSelector);
			if (custom) return custom;
		}

		// Check for default selector on page
		const defaultContainer = document.querySelector('.tcms-error-summary');
		if (defaultContainer) return defaultContainer;

		// Create one at the bottom of the form
		let container = this.form.querySelector('.tcms-error-summary-auto');
		if (!container) {
			container = document.createElement('div');
			container.className = 'tcms-error-summary tcms-error-summary-auto';
			this.form.appendChild(container);
		}
		return container;
	}

	showErrorSummary(invalidFields) {
		const container = this.getErrorSummaryContainer();
		container.innerHTML = '';
		container.style.display = '';

		if (invalidFields.length === 0) {
			container.style.display = 'none';
			return;
		}

		const list = document.createElement('ul');
		list.className = 'tcms-error-summary-list';

		invalidFields.forEach(field => {
			const item = document.createElement('li');
			const link = document.createElement('a');
			link.href = '#';
			link.className = 'tcms-error-summary-link';
			link.textContent = field.label || field.property;

			// Add validation message if available
			if (field.input.validationMessage) {
				const message = document.createElement('span');
				message.className = 'tcms-error-summary-message';
				message.textContent = ` - ${field.input.validationMessage}`;
				link.appendChild(message);
			}

			link.addEventListener('click', (e) => {
				e.preventDefault();
				this.scrollToField(field);
			});

			item.appendChild(link);
			list.appendChild(item);
		});

		container.appendChild(list);
	}

	clearErrorSummary() {
		const container = this.form.querySelector('.tcms-error-summary-auto');
		if (container) {
			container.innerHTML = '';
			container.style.display = 'none';
		}

		// Also clear custom containers
		const customSelector = this.form.dataset.errorSummary;
		if (customSelector) {
			const custom = document.querySelector(customSelector);
			if (custom) {
				custom.innerHTML = '';
				custom.style.display = 'none';
			}
		}
	}

	scrollToField(field) {
		// Dispatch event so step-based forms can navigate to the correct step
		this.form.dispatchEvent(new CustomEvent('tcms:error-navigate', {
			bubbles: true,
			detail: { field: field, property: field.property }
		}));

		// Scroll field into view
		field.container.scrollIntoView({ behavior: 'smooth', block: 'center' });

		// Focus the input after scroll completes
		setTimeout(() => {
			field.input.focus();
			field.input.reportValidity();
		}, 300);
	}

	closeDialog() {
		const dialog = this.form.querySelector("dialog[open]");
		if (dialog) {
			dialog.dialog ? dialog.dialog.close() : dialog.close();
		}
	}

	focusFirstInput() {
		const selector   = 'input:not([type="hidden"]):not([readonly]):not([disabled]), textarea:not([readonly]):not([disabled]), select:not([disabled])';
		const candidates = Array.from(this.form.querySelectorAll(selector));
		// Skip inputs nested in <dialog> elements — those belong to image/file/deck-item edit modals
		const first = candidates.find(input => !input.closest('dialog'));
		first?.focus();
	}

	save() {
		// Defensive prune: drop any fields whose container was detached from the
		// DOM. refreshFields() handles the normal case, but external mutations
		// (e.g. show/hide plugins that fire their event before the actual node
		// removal) can leave stale references in this.fields and crash validate()
		// or generateData() on dead nodes.
		this.fields = this.fields.filter(field => this.form.contains(field.container));

		if (!this.validate()) {
			this.validated = false;
			this.error('Please fix validation errors before saving.');
			return;
		}
		if (this.isError()) {
			this.error('Error in form, cannot save.');
			return;
		}
		this.validated = true;
		this.closeDialog();
        this.processing();
        this.api.postAPI(this.route, this.generateData(), this.method)
            .then(response => this.afterSave(response))
            .catch(error => this.error(error));
    }

    async delete() {
        // Only delete if editing object (templates always allow delete since they skip edit mode)
        if (!this.isEditMode() && !this.isTemplateForm()) return;

        // A no-delete-confirm class on the form (or an ancestor) skips the dialog —
        // e.g. frontend pin/unpin flows where the visitor deletes their own object.
        if (await tcmsConfirm({ message: t("confirm.delete_item"), element: this.form })) {
            this.validated = true;
            this.processing();

			let deleteAPI = `/collections/${this.collection}/${this.id}`;

			// if (this.isDeckForm()) {
			// 	deleteAPI = `/{collection}/${this.id}/{property}/deck/{itemId}`;
			// }
			if (this.isSchemaForm()) {
				deleteAPI = `/schemas/${this.id}`;
			}
			if (this.isCollectionForm()) {
				deleteAPI = `/collections/${this.id}`;
			}
			if (this.isTemplateForm()) {
				deleteAPI = this.route;
			}

            this.api.postAPI(deleteAPI, {}, "DELETE")
                .then(response => this.runDeleteActions(response))
                .catch(error => this.error(error));
        }
    }

    afterSave(response) {
        if (!response) return;

		// Adopt the saved id BEFORE flushing deferred uploads. Add-only forms with
		// an autogen id have no id field, so queued file/image droplets would post
		// to a URL with an empty id and fail. saveDroplets() rebuilds each upload
		// URL from getId(), so the id must be set first. We deliberately do NOT
		// enter edit mode here: afterSaveAction() selects new-vs-edit post-save
		// actions from isEditMode(), and a fresh add must still run the new actions.
		const savedId = this.responseId(response);
		if (savedId && (!this.id || this.id.length === 0)) {
			this.setId(savedId);
		}

		const unsavedDroplets = this.droplets.filter(field => field.isUnsaved());
        if (unsavedDroplets.length > 0) {
            return this.saveDroplets(() => this.afterSaveAction(response));
        }
		this.afterSaveAction(response);
    }

    afterSaveAction(response) {
		// Templates skip edit mode but still need to run edit actions when updating
		const runEditActions = this.isEditMode() || this.isTemplateEditMode();

		// Email verification flow: public registration with requireEmailVerification
		// returns the saved user wrapped with meta.requiresVerification=true. The
		// user is NOT logged in — instead of running post-save actions (which
		// usually redirect/reload), reveal a designated message element and
		// dispatch an event so authors can wire up custom behavior.
		if (response?.meta?.requiresVerification === true) {
			this.handleVerificationRequired(response);
			return;
		}

		// Extract ID from response for new objects (needed for actions like
		// redirect-object). Normally already set in afterSave(); kept for the
		// direct-call path and as a guard.
		const savedId = this.responseId(response);
		if (savedId && (!this.id || this.id.length === 0)) {
			this.setId(savedId);
		}
		this.setupEditMode();

		// Mark all fields as saved
		this.fields.forEach(field => field.saved());

		// Stash any warnings from this response so runAction's showSuccessAndWait
		// can include them when it fires the success event before navigating away.
		this._savedResponse = response || null;

		// Run actions first, then show success banner
		const actions = runEditActions ? this.settings.actions.edit : this.settings.actions.new;
		this.runActions(actions)
			.then(() => this.success(response))
			.catch(() => {
				// Error already handled in runActions
			});
    }

	/**
	 * Handle a public-registration response that requires email verification.
	 * Hides the form, reveals any element marked `[data-verification-message]`
	 * within the form (or its parent), and dispatches a custom event so
	 * authors can hook in with redirects/analytics/custom UX.
	 *
	 * @param {object} response - The save response (includes meta.requiresVerification)
	 */
	handleVerificationRequired(response) {
		// Mark fields saved so dirty-state cleanup still happens.
		this.fields.forEach(field => field.saved());

		// Hide the form fields so the user doesn't try to submit again.
		this.form.classList.add("verification-required");

		// Reveal a designated message element. Look inside the form first,
		// then in the parent — both placements are valid (form-internal or
		// adjacent).
		const scopes = [this.form, this.form.parentElement].filter(Boolean);
		for (const scope of scopes) {
			const el = scope.querySelector("[data-verification-message]");
			if (el) {
				el.hidden = false;
				el.removeAttribute("hidden");
				break;
			}
		}

		// Dispatch event for custom behavior — redirects, analytics, etc.
		this.form.dispatchEvent(new CustomEvent("cms:form:verification-required", {
			bubbles : true,
			detail  : { response },
		}));
	}

    async runAction(action) {
		// Helper to show success banner and wait before navigation (default: true).
		// Pass the stashed response so meta.warnings reach the status banner even
		// when the action navigates away before the post-actions success() call fires.
		const showSuccessAndWait = async () => {
			if (action.showSuccess !== false) {
				this.success(this._savedResponse);
				await new Promise(resolve => setTimeout(resolve, this.delayActions));
			}
		};

        switch (action.action) {
			case "back":
				await showSuccessAndWait();
				const referrerUrl = new URL(document.referrer);
				if (window.history.length > 1 && document.referrer && referrerUrl.hostname === window.location.hostname) {
					document.location = document.referrer;
				}
				break;
			case "mailer":
				await this.api.postAPI('/action/mailer', {
					data     : this.generateData(),
					mailerId : action.mailerId,
				});
				break;
            case "redirect":
				await showSuccessAndWait();
                document.location = action.link;
                break;
            case "redirect-object":
				await showSuccessAndWait();
				const link = decodeURI(action.link);
				if (link.match("{id}"))  {
					document.location = link.replace("{id}", this.id);
				} else {
					document.location = link + this.id;
				}
                break;
            case "refresh":
				await showSuccessAndWait();
                location.reload(true);
                break;
			case "ajax":
			case "webhook":
				const response = await fetch(action.link, {
					method  : 'POST',
					mode    : 'cors',
					// Same-origin targets are session-authenticated API routes that
					// now require the CSRF token. Third-party webhooks must not get
					// the header — it would force a preflight they can't answer.
					headers : Object.assign({ 'Content-Type': 'application/json' }, csrfHeadersFor(action.link)),
					body    : JSON.stringify(this.generateData()),
				});
				if (!response.ok) {
					throw new Error(`Action ${action.action} failed: ${response.statusText}`);
				}
				break;
			default:
				if (this.extensionActions[action.action]) {
					const { action: _type, ...config } = action;
					await this.api.postAPI(this.extensionActions[action.action], {
						data: this.generateData(),
						...config,
					});
				} else {
					console.warn(`Unknown action type: ${action.action}`);
				}
        }
    }

	/**
	 * Run an array of actions sequentially.
	 * By default, if an action fails, subsequent actions won't execute.
	 * Set `continue: true` on an action to continue execution even if it fails.
	 *
	 * @param {Array} actions - Array of action objects to execute
	 */
	async runActions(actions) {
		if (!Array.isArray(actions) || actions.length === 0) {
			this.form.classList.add("actions-completed");
			return;
		}

		for (const action of actions) {
			try {
				await this.runAction(action);
			} catch (error) {
				console.error(`Action execution failed:`, error);

				// If continue is true, log the error but keep going
				if (action.continue === true) {
					console.warn(`Action failed but continuing due to continue: true`, action);
					continue;
				}

				// Otherwise, stop execution and show error
				this.error(error.message || 'Action execution failed');
				throw error; // Re-throw to prevent further execution
			}
		}

		// All actions completed successfully
		this.form.classList.add("actions-completed");
		this.form.dispatchEvent(new CustomEvent("actions-completed"));
	}

    runNewActions() {
		setTimeout(() => this.runActions(this.settings.actions.new), this.delayActions);
    }

    runEditActions() {
		setTimeout(() => this.runActions(this.settings.actions.edit), this.delayActions);
    }

	runDeleteActions() {
		setTimeout(() => this.runActions(this.settings.actions.delete), 250);
    }

    //-------------------------
    // Form States
    //-------------------------
	isEditMode() {
		// Template forms support rename/move — PHP sets the route and method,
		// so skip edit mode to keep the ID field editable
		if (this.isTemplateForm()) return false;

		return (["PUT", "PATCH"].includes(this.method.toUpperCase()) && this.form.classList.contains("edit-mode"));
    }

    setupEditMode() {
		if (this.isEditMode()) return;

		// Only setup edit mode for forms that have an ID (object/collection/schema forms)
		// Simple forms without IDs should always use POST
		if (!this.id || this.id.length === 0) return;

		// Deck forms have their route fully set by PHP (includes parent object ID and optionally item ID)
		// Don't modify the route for deck forms
		if (this.type === "deck") return;

		// Set the method to PUT or PATCH for editing existing objects
		this.method = this.form.dataset.method||"PUT";
		if (this.method.toUpperCase() === "POST") this.method = "PUT";

		this.form.classList.add("edit-mode");

		// Update the API to the edit endpoint
		if (!this.route.endsWith(this.id)) {
			this.route = `${this.route}/${this.id}`;
			this.form.dataset.route = this.route;
		}
    }

	setupFieldsForEdit() {
		if (!this.isEditMode()) {
			return;
		}

		// The ID cannot be changed in edit form
		const idField = this.fields.filter(field => field.property === "id").shift();
		// idField may be undefined when the schema has no `id` property,
		// or when the field was filtered out before reaching us — optional
		// chaining keeps a missing field from blowing up the form init.
		if (idField?.disable) {
			idField.disable();
		}
		// lock() is only defined on the Identifier field; the id field can
		// also render as a plain hidden/text field (base TotalField), which
		// has disable() but no lock(). Guard it the same way disable() is
		// guarded so the unguarded call can't throw and abort form init
		// (which would leave Save/dirty-tracking unwired on edit forms).
		if (idField?.lock) {
			idField.lock();
		}

		// Update the droplets to autoupload
		this.droplets.forEach(field => field.droplet.autoProcessQueue());
    }

	uploadComplete() {
		if (this.isUnsaved()) return;
		this.success();
	}

	changeState(newState, details = {}) {
		this.state = newState;
		if (newState) {
			this.form.classList.add(newState);
			this.form.dispatchEvent(new CustomEvent(newState, {detail: details}));
		}
		// Filter the newState and remove all others
		const remove = this.states.filter(e => e !== newState);
		this.form.classList.remove(...remove);

		// Clear actions-completed when starting a new save cycle or on error
		// (but not when transitioning to success, since actions-completed comes after success)
		if (newState !== "success") {
			this.form.classList.remove("actions-completed");
		}
	}

	clear() {
		this.changeState(null);
	}

	unsaved() {
		this.changeState("unsaved");
	}

	isUnsaved() {
		let unsaved = false;
		this.fields.forEach(field => { if (field.isUnsaved()) unsaved = true; });
        return unsaved;
    }

	processing() {
		this.changeState("processing");
    }

	isProcessing() {
        return this.state === "processing";
    }

    error(error, field = null) {
		this.changeState("error", {error:error});
		this.validated = false;

		// Include field information in error message if available
		if (field) {
			const fieldLabel = field.label || field.property || 'unknown field';
			const fieldProperty = field.property || 'unknown';
			console.error(`Form Error [${fieldProperty}]:`, error);
			console.error(`  Field Label: "${fieldLabel}"`);
			console.error(`  Field Element:`, field.input);
		} else {
			console.error("Form Error:", error);
		}
    }

	isError() {
		return this.state === "error";
	}

    success(response = null) {
		// Extract ID from response for new objects
		const savedId = this.responseId(response);
		if (savedId && (!this.id || this.id.length === 0)) {
			this.setId(savedId);
		}
		this.setupEditMode();
		this.changeState("success", response);
		this.validated = false;
		this.fields.forEach(field => field.saved());
    }

	isSuccess() {
		return this.state === "success";
	}


    //-------------------------
    // Droplet Interactions
    //-------------------------

    // We only want to process the droplet queue after the inital
    // post request to create the object has been saved
    saveDroplets(callback) {

        let dropletCount = this.droplets.filter(field => field.isUnsaved()).length;

        // If no unsaved droplets, execute callback immediately
        if (dropletCount === 0) {
            if (typeof callback === "function") callback();
            return;
        }

        const dropletComplete = (callback) => {
            // When there are multiple droplets, we need to ensure that the callback
            // is only processed once for the entire form submission.
            // Example: if there are 3 droplets, run after the 3rd time this has been triggered
            dropletCount--;
            if (dropletCount === 0) {
                if (typeof callback === "function") callback();
            }
        };

		this.droplets.forEach(field => {
			if (!field.isUnsaved()) return; // skip if the droplet is not unsaved
			field.updateAPIUrl(); // update the droplet API URL
			const droplet = field.droplet;
            if (droplet.isComplete()) {
                dropletComplete(callback);
                return;
            }
            droplet.onQueueComplete(() => dropletComplete(callback));
            droplet.autoProcessQueue(); // Enable auto processing for all queued files
            droplet.processQueue();
		});
    }

    //-------------------------
    // Generating Form Data
    //-------------------------

    generateData() {
        const data = {};
		this.fields.forEach(field => {
			if (field.isSubField()) return; // skip subfields
			data[field.property] = field.getValue();
		});
        return data;
    }
}
